import { Box, Chip, LinearProgress, Paper, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { ELinks } from '@sharedInterfaces/globalEnums';
import getEmployees from '@src/APIs/graphQl/Employee/getEmployees';
import EmployeeCard, {
    type IEmployeeCardConfig,
    type IEmployeeCardData,
} from '@src/App/Components/Core/Cards/EmployeeCard/EmployeeCard';
import LandingPage, {
    type ILandingPageFeatureCard,
    type ILandingPageQuickLink,
    ILandingPageStep,
    type ILandingPageWidget,
} from '@src/App/Components/Core/Layouts/LandingPage/LandingPage';
import MvpSubNav, { type IMvpQuickAction, type IMvpRole } from '@src/App/Components/Core/MvpSubNav/MvpSubNav';
import { getPrimaryRoleTitle } from '@src/App/Helper/roleHelper';
import { EIconCategory, getAppIcon } from '@src/helper/iconRegistry';
import type { AppState } from '@store/store';
import
    {
        Award,
        BarChart3,
        Calendar,
        ClipboardList,
        Grid3X3,
        List,
        MessageSquare,
        Settings,
        Target,
        TrendingUp,
        User,
        UserPlus,
        Users,
    } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

function MyTeamLandingPage()
{
    const theme = useTheme();
    const navigate = useNavigate();
    const orgUnits = useSelector((state: AppState) => state.OUs);
    const currentUser = useSelector((state: AppState) => state.employee);
    const employees = useSelector((state: AppState) => state.employees);
    const featureFlags = useSelector((state: AppState) => state.company.featureFlags);

    const [activeRole, setActiveRole] = useState('manager');
    const [viewMode, setViewMode] = useState<'stats' | 'cards'>('stats');

    const teamMemberIds = useMemo(() =>
    {
        const myOrgUnits = orgUnits.filter((ou) => ou.leader === currentUser.id);
        return myOrgUnits
            .flatMap((ou) => ou.employees || [])
            .map((emp: number | { id: number }) => (typeof emp === 'number' ? emp : emp.id))
            .filter((empId) => empId !== currentUser.id);
    }, [orgUnits, currentUser.id]);

    useEffect(() =>
    {
        getEmployees().catch((error: Error) =>
        {
            console.error('Failed to load employees:', error);
        });
    }, []);

    const teamMembers = useMemo(() =>
    {
        const myOrgUnits = orgUnits.filter((ou) => ou.leader === currentUser.id);

        return employees
            .filter((emp) => teamMemberIds.includes(emp.id))
            .filter((emp) => emp.id !== currentUser.id)
            .map((emp) =>
            {
                const nameParts = (emp.title ?? '').trim().split(' ');
                const firstName = nameParts[0] || '';
                const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
                const availability =
                    typeof emp.availability === 'number'
                        ? emp.availability
                        : emp.availability?.availability || 0;
                const roles = emp.roles || [];

                return {
                    id: emp.id,
                    name: emp.title,
                    firstName,
                    lastName,
                    title: emp.title,
                    pictureId: emp.pictureId,
                    email: emp.email,
                    availability,
                    skills: emp.skills || [],
                    roles,
                    role: getPrimaryRoleTitle(roles) || 'Rolle nicht definiert',
                    projects: emp.projects || [],
                    orgUnit: myOrgUnits.find((ou) => ou.employees.some((e: number | { id: number }) => (typeof e === 'number' ? e : e.id) === emp.id))?.title || 'Unbekannt',
                };
            });
    }, [employees, teamMemberIds, orgUnits, currentUser.id]);

    const teamSize = teamMembers.length;
    const activeProjects = teamMembers.reduce((sum, member) => sum + (member.projects?.length || 0), 0);
    const teamAvailability =
        teamMembers.length > 0
            ? Math.round(teamMembers.reduce((sum, member) => sum + member.availability, 0) / teamMembers.length)
            : 0;

    const roles: IMvpRole[] = [
        {
            id: 'manager',
            label: 'Manager',
            icon: getAppIcon(EIconCategory.TEAMS),
        },
        {
            id: 'admin',
            label: 'Admin',
            icon: getAppIcon(EIconCategory.SETTINGS),
        },
    ];

    const quickActions: IMvpQuickAction[] = [
        {
            id: 'team-manage',
            label: 'Team verwalten',
            icon: getAppIcon(EIconCategory.TEAMS),
            onClick: () => navigate(`/${ELinks.MY_TEAM_MANAGE}`),
            variant: 'primary',
        },
        {
            id: 'employees',
            label: 'Mitarbeitende',
            icon: getAppIcon(EIconCategory.EMPLOYEES),
            onClick: () => navigate(`/${ELinks.PEOPLE_EMPLOYEES}`),
        },
    ];

    const featureCards: ILandingPageFeatureCard[] = [
        {
            id: 'team-overview',
            title: 'Team Übersicht',
            description: 'Vollständige Übersicht aller Teammitglieder mit Skills und Verfügbarkeiten',
            icon: <Users size={24} />,
            imagePath: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=400&h=200&fit=crop',
            onClick: () => navigate(`/${ELinks.MY_TEAM_MANAGE}`),
        },
        {
            id: 'skill-matrix',
            title: 'Skill-Matrix',
            description: 'Intelligente Analyse der Team-Kompetenzen und Identifikation von Skill-Gaps',
            icon: <BarChart3 size={24} />,
            imagePath: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=200&fit=crop',
            onClick: () => console.log('Skill Matrix'),
        },
        {
            id: 'resource-planning',
            title: 'Ressourcenplanung',
            description: 'Optimale Zuordnung von Teammitgliedern zu Projekten basierend auf Skills',
            icon: <Calendar size={24} />,
            imagePath: 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=400&h=200&fit=crop',
            onClick: () => console.log('Resource Planning'),
        },
        {
            id: 'development',
            title: 'Team-Entwicklung',
            description: 'Gezielte Weiterbildung und Skill-Entwicklung für nachhaltigen Teamerfolg',
            icon: <TrendingUp size={24} />,
            imagePath: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=200&fit=crop',
            onClick: () => console.log('Team Development'),
        },
    ];

    const quickLinks: ILandingPageQuickLink[] = [
        {
            id: 'manage-team',
            title: 'Team verwalten',
            description: 'Teammitglieder hinzufügen, bearbeiten und organisieren',
            icon: <Users size={28} />,
            variant: 'primary',
            onClick: () => navigate(`/${ELinks.MY_TEAM_MANAGE}`),
        },
        {
            id: 'skill-analysis',
            title: 'Skill-Matrix anzeigen',
            description: 'Detaillierte Analyse der Team-Kompetenzen und Skill-Verteilung',
            icon: <BarChart3 size={28} />,
            variant: 'secondary',
            onClick: () => console.log('Skill Matrix'),
        },
    ];

    const renderTeamStatsWidget = () => (
        <Stack spacing={3}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Chip label={`${teamSize} Teammitglieder`} color="primary" variant="outlined" size="medium" />
                <Chip label={`${activeProjects} Aktive Projekte`} color="success" variant="outlined" size="medium" />
                <Chip label={`${teamAvailability}% Verfügbarkeit`} color="info" variant="outlined" size="medium" />
            </Box>

            {teamMembers.length > 0 && (
                <Stack spacing={2}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Teammitglieder
                        </Typography>
                        <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={(_, newMode) => newMode && setViewMode(newMode)}
                            size="small"
                        >
                            <ToggleButton value="stats">
                                <List size={16} />
                            </ToggleButton>
                            <ToggleButton value="cards">
                                <Grid3X3 size={16} />
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    {viewMode === 'stats' ? (
                        <Stack spacing={1}>
                            {teamMembers.slice(0, 3).map((member) => (
                                <Paper
                                    key={member.id}
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            borderColor: theme.palette.primary.main,
                                            transform: 'translateY(-2px)',
                                        },
                                    }}
                                    onClick={() => navigate(`/${ELinks.PEOPLE_EMPLOYEES}/${member.id}`)}
                                >
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Box
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: '50%',
                                                bgcolor: 'primary.main',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: 'white',
                                                fontWeight: 600,
                                            }}
                                        >
                                            {member.title?.split(' ')[0]?.charAt(0)}
                                            {member.title?.split(' ').slice(-1)[0]?.charAt(0)}
                                        </Box>
                                        <Box sx={{ flexGrow: 1 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                {member.title}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {member.availability}% verfügbar
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Paper>
                            ))}
                        </Stack>
                    ) : (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: '1fr', md: 'repeat(3, 1fr)' },
                                gap: 2,
                            }}
                        >
                            {teamMembers.slice(0, 3).map((member) =>
                            {
                                const employeeData: IEmployeeCardData = {
                                    id: member.id,
                                    title: member.title,
                                    pictureId: member.pictureId,
                                    email: member.email,
                                    jobTitle: member.role,
                                    availability: member.availability,
                                    skills:
                                        member.skills
                                            ?.slice(0, 3)
                                            .map((skill) => ({ title: skill.title || 'Skill' })) || [],
                                    projects:
                                        member.projects?.map((project) => ({
                                            title: project.title,
                                            active: project.active,
                                        })) || [],
                                    location: 'Remote',
                                };

                                const cardConfig: IEmployeeCardConfig = {
                                    variant: 'team',
                                    showAvailability: true,
                                    showSkills: true,
                                    showProjects: true,
                                    showLocation: true,
                                    maxSkills: 3,
                                    primaryAction: {
                                        label: 'Zum Profil',
                                        onClick: () => navigate(`/${ELinks.PEOPLE_EMPLOYEES}/${member.id}`),
                                        variant: 'contained',
                                    },
                                    secondaryAction: {
                                        label: 'Nachricht',
                                        onClick: () => console.log('Send message'),
                                    },
                                };

                                return (
                                    <EmployeeCard
                                        key={member.id}
                                        employee={employeeData}
                                        config={cardConfig}
                                        featureFlags={featureFlags}
                                    />
                                );
                            })}
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Chip
                            label="Zum Team"
                            color="primary"
                            variant="filled"
                            clickable
                            onClick={() => navigate(`/${ELinks.MY_TEAM_MANAGE}`)}
                            sx={{
                                px: 3,
                                py: 1,
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: theme.shadows[4],
                                },
                            }}
                        />
                    </Box>
                </Stack>
            )}
        </Stack>
    );

    const widgets: ILandingPageWidget[] = [
        {
            id: 'team-stats',
            title: 'Team Statistiken',
            content: renderTeamStatsWidget(),
        },
    ];

    return (
        <>
            <MvpSubNav
                currentPageTitle="Mein Team"
                roles={roles}
                activeRole={activeRole}
                onRoleChange={setActiveRole}
                quickActions={quickActions}
            />

            <LandingPage
                title="Mein Team"
                subtitle="Kollaborative Teamverwaltung & Skill-Entwicklung"
                description="Verwalten Sie Ihr Team effizient mit intelligenten Tools für Skill-Management, Verfügbarkeitsplanung und Performance-Tracking."
                heroIcon={
                    <Box
                        component="img"
                        src="/t4loOpportunitiesIllustration.png"
                        alt="Opportunities Illustration"
                        sx={{
                            maxWidth: '100%',
                            height: 'auto',
                            boxShadow: 'none',
                            filter: 'none',
                        }}
                    />
                }
                processTitle="Effektives Team-Management"
                featureCards={featureCards}
                quickLinksTitle="Schnellzugriff"
                quickLinks={quickLinks}
                widgets={widgets}
            />
        </>
    );
}

export default MyTeamLandingPage;
